import React from 'react';
import { Truck, Route, MapPinned, Warehouse, AlertTriangle } from 'lucide-react';
import DirectServiceTemplate from '../components/DirectServiceTemplate';

const accent = {
  heroBg: 'bg-[radial-gradient(circle_at_top,rgba(245,158,11,0.14),transparent_70%)]',
  pill: 'bg-amber-50 text-amber-700 border-amber-100',
  cta: 'bg-[#b45309] hover:bg-[#92400e] shadow-amber-900/15',
  kpiBox: 'bg-amber-50 border-amber-100 text-amber-700',
  polyline: '#b45309',
  inputFocus: 'focus:border-amber-400',
  orderCta: 'bg-amber-500 hover:bg-amber-600 text-[#1A3062]',
  packageActive: 'border-amber-200 bg-amber-50/40',
  toggleOn: 'border-amber-200 bg-amber-50/40',
  switchTrack: 'bg-amber-500 border-amber-500',
  switchKnobOn: 'bg-white translate-x-0',
  stepBox: 'bg-amber-50 border-amber-100 text-amber-700',
  modalHeader: 'bg-gradient-to-l from-amber-700 to-orange-600',
};

const meta = {
  Icon: Truck,
  badge: 'خدمات مستقیم — جاده‌ای',
  title: 'جاده‌ای',
  subtitle:
    'برای تحویل درب-به-درب و انعطاف بالا: تخصیص ناوگان، مدیریت زمان‌بندی بارگیری/تخلیه، کنترل مسیر و نقاط توقف، و امکان اتصال به ریلی/هوایی/دریایی در حمل چندوجهی.',
  quickTitle: 'درب-به-درب با زمان پاسخ سریع',
  quickDesc: 'مناسب برای مسیرهای داخلی/مرزی و نیاز به انعطاف زمانی. امکان مدیریت ریسک توقف و اعلان‌های مرحله‌ای.',
  sla: '۲ تا ۱۲ ساعت',
  baseDays: 3,
  baseCost: 90,
  placeholders: {
    origin: 'مثلاً: مرز آستارا / انبار مبدا',
    destination: 'مثلاً: قزوین / انبار مقصد',
    cargo: 'مثلاً: مواد اولیه / کالاهای عمومی / حساس',
  },
};

const packages = [
  {
    key: 'economy',
    title: 'اقتصادی',
    desc: 'زمان‌بندی منعطف، مناسب بارهای عمومی و برنامه‌محور.',
    badge: 'کم‌هزینه',
    badgeCls: 'bg-amber-50 text-amber-700 border-amber-100',
    baseDays: 4,
    baseCost: 70,
  },
  {
    key: 'standard',
    title: 'استاندارد',
    desc: 'تعادل زمان/هزینه با کنترل نقاط توقف و تحویل.',
    badge: 'پیشنهادی',
    badgeCls: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    baseDays: 3,
    baseCost: 90,
  },
  {
    key: 'priority',
    title: 'اکسپرس',
    desc: 'اولویت تخصیص ناوگان و پاسخ سریع‌تر (وابسته به ظرفیت).',
    badge: 'سریع‌تر',
    badgeCls: 'bg-blue-50 text-blue-700 border-blue-100',
    baseDays: 2,
    baseCost: 120,
  },
];

const capabilities = [
  {
    title: 'درب-به-درب و برنامه‌ریزی',
    desc: 'هماهنگی بارگیری/تخلیه و تخصیص ناوگان متناسب با کالا.',
    items: ['کامیون/تریلی', 'نوبت‌دهی', 'پیکاپ/تحویل', 'SLA تحویل'],
  },
  {
    title: 'کنترل مسیر و توقف',
    desc: 'کاهش ریسک توقف با مدیریت نقاط کنترل و اعلان‌ها.',
    items: ['مانیتورینگ مسیر', 'اعلان توقف', 'ETA', 'کنترل مدارک'],
  },
  {
    title: 'خدمات جانبی',
    desc: 'خدمات ارزش افزوده برای کاهش خطا و زمان.',
    items: ['انبارداری', 'Cross-dock', 'بیمه', 'بسته‌بندی/برچسب'],
  },
];

const documents = [
  {
    title: 'بارنامه جاده‌ای',
    desc: 'اطلاعات فرستنده/گیرنده، مسیر، وزن و شرایط حمل.',
    state: 'الزامی',
    stateCls: 'bg-white text-gray-700 border-gray-100',
    tags: ['اسناد حمل', 'جاده‌ای'],
  },
  {
    title: 'مجوزهای عبور/مرزی (در صورت نیاز)',
    desc: 'در حمل مرزی/ترانزیتی ممکن است مجوزهای تکمیلی لازم باشد.',
    state: 'وابسته به مسیر',
    stateCls: 'bg-amber-50 text-amber-700 border-amber-100',
    tags: ['مرز', 'مجوز'],
  },
  {
    title: 'چک‌لیست ایمنی بارگیری',
    desc: 'برای کاهش خسارت و توقف‌های ناخواسته.',
    state: 'پیشنهادی',
    stateCls: 'bg-blue-50 text-blue-700 border-blue-100',
    tags: ['ایمنی', 'کنترل ریسک'],
  },
  {
    title: 'بیمه‌نامه',
    desc: 'پیشنهادی برای کالاهای ارزشمند یا مسیرهای پرریسک.',
    state: 'پیشنهادی',
    stateCls: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    tags: ['بیمه', 'ریسک'],
  },
];

const processSteps = [
  { title: 'ثبت RFQ', desc: 'مبدا/مقصد، نوع کالا، وزن/حجم و زمان‌بندی را اعلام کنید.' },
  { title: 'تخصیص ناوگان', desc: 'انتخاب خودرو و راننده/اپراتور بر اساس ظرفیت و SLA.' },
  { title: 'بارگیری و کنترل ایمنی', desc: 'کنترل چیدمان، پلمب (در صورت نیاز) و ثبت مدارک.' },
  { title: 'رهگیری مسیر', desc: 'مانیتورینگ مسیر، اعلان توقف‌ها و به‌روزرسانی ETA.' },
  { title: 'تحویل و تسویه', desc: 'ثبت تحویل، کنترل مغایرت، و انجام تسویه/صورتحساب.' },
];

const providers = [
  {
    name: 'ناوگان درب-به-درب (داخلی)',
    Icon: Route,
    badges: ['درب-به-درب', 'SLA', 'انعطاف'],
    note: 'مناسب برای ارسال‌های داخلی و تحویل سریع‌تر با کنترل نقاط توقف.',
    score: 4.5,
    scoreCls: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    response: '۲ تا ۶ ساعت',
    coverage: 'ملی',
    capacity: 'بالا',
  },
  {
    name: 'حمل مرزی/ترانزیتی',
    Icon: MapPinned,
    badges: ['مرز', 'ترانزیت', 'مجوز'],
    note: 'برای مسیرهای مرزی با تمرکز روی کنترل مدارک و کاهش توقف.',
    score: 4.2,
    scoreCls: 'bg-amber-50 text-amber-700 border-amber-100',
    response: '۶ تا ۱۲ ساعت',
    coverage: 'مرزها',
    capacity: 'متوسط',
  },
  {
    name: 'انبارداری و Cross-dock',
    Icon: Warehouse,
    badges: ['تفکیک/تجمیع', 'برچسب', 'بسته‌بندی'],
    note: 'برای تجمیع، آماده‌سازی و تحویل مرحله‌ای به مقصد.',
    score: 4.1,
    scoreCls: 'bg-gray-50 text-gray-700 border-gray-100',
    response: 'کمتر از ۱ روز',
    coverage: 'شهرهای اصلی',
    capacity: 'قابل افزایش',
  },
  {
    name: 'کنترل ریسک مسیر (پیشنهادی)',
    Icon: AlertTriangle,
    badges: ['هشدار', 'کنترل توقف', 'KPI'],
    note: 'پیشنهاد برای مسیرهای حساس: تعریف نقاط کنترل و اعلان‌های وضعیت.',
    score: 4.0,
    scoreCls: 'bg-blue-50 text-blue-700 border-blue-100',
    response: '—',
    coverage: 'مسیرهای منتخب',
    capacity: '—',
  },
];

const hubs = [
  { name: 'مرز آستارا', lat: 38.43, lng: 48.87, note: 'ورود/خروج مرزی' },
  { name: 'رشت', lat: 37.28, lng: 49.58, note: 'گره شمالی' },
  { name: 'قزوین', lat: 36.27, lng: 50.0, note: 'گره میانی' },
  { name: 'تهران', lat: 35.72, lng: 51.4, note: 'توزیع و تحویل' },
];

const faqs = [
  { q: 'چه زمانی جاده‌ای بهترین انتخاب است؟', a: 'زمانی که تحویل درب-به-درب و انعطاف در زمان‌بندی اولویت دارد.' },
  { q: 'چطور توقف‌ها کاهش پیدا می‌کند؟', a: 'با کنترل مدارک قبل از حرکت، تعریف نقاط کنترل و اطلاع‌رسانی مرحله‌ای.' },
  { q: 'برای بار مرزی چه نکاتی مهم است؟', a: 'مجوزها، زمان‌بندی مرز، و تطبیق اسناد برای جلوگیری از توقف.' },
  { q: 'آیا امکان ترکیب با ریلی/دریایی/هوایی هست؟', a: 'بله، جاده‌ای معمولاً نقش پیکاپ/تحویل نهایی را در حمل چندوجهی دارد.' },
];

export default function DirectRoad() {
  return (
    <DirectServiceTemplate
      meta={meta}
      accent={accent}
      formDefaults={{
        origin: 'مرز آستارا',
        destination: 'قزوین',
        cargo: 'مواد اولیه (کیسه)',
        incoterm: 'FCA',
        weightKg: 26500,
        volumeM3: 42,
        isExpress: false,
        needsInsurance: true,
        isSpecialHandling: false,
      }}
      packages={packages}
      capabilities={capabilities}
      documents={documents}
      processSteps={processSteps}
      providers={providers}
      hubs={hubs}
      faqs={faqs}
    />
  );
}
